/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import ContentShell from "components/ContentShell";
import { SimpleSelectInput } from "components/FormSelectInput";
import { SimpleTextInput } from "components/FormTextInput";
import { useSnackbar } from "hooks/useSnackbar";
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import configData from "config/config.json";
import useStyles from "styles/pack/packEditor";
import {
  createPack2,
  getBoosters,
  getPackImages,
  getPlayerPacks,
  packInfo,
  updatePack2,
  validatePackSlots,
} from "services/packServices";
import {
  AddAndDelete,
  BoosterSlot,
  buildLabel,
  NamedPlayerSlot,
  PlayerPoolSlot,
  PlayerSlot,
} from "./Slots";
import { deepCopy, getUniquesArrayObject } from "utils/miscUtils";
import sessionHelper from "services/sessionHelper";
import FilePicker from "components/FilePicker";
import { defaultPageNo, defaultPageSize } from "utils/constants";
import { packSchema } from "utils/validation";
import {
  uploadImageV2,
  uploadContentTranslations,
} from "services/uploadService";
import ViewLanguageModal from "screens/modals/ViewLanguageModal";
import useOpenClose from "hooks/useOpenClose";
import BulkTranslationUpload from "components/BulkTranslationUpload";
import TranslationButtons from "components/TranslationButtons";
import { getPackCategories } from "services/packCategoryServices";
import SearchableInput from "components/SearchableInput";
import { getPlayerCards } from "services/playerCardService";
import debounce from "lodash.debounce";
import { SimpleMultiSelectInput } from "components/FormMultiSelectInput";
import { InputShell } from "components/FormInputShell";
import CustomChip from "components/CustomChip";
import CustomButton from "components/CustomButton";
import { PackValidationDialog } from "components/PackValidationDialog";
import { DeleteOutlineRounded } from "@mui/icons-material";
import { PermittedRoles } from "utils/permissions";
import { ImageType, DEFAULT_IMAGE_DIMENSIONS } from "utils/imageConstants";

const PlayerAndBooster = ["Player", "Booster"];

const operatorPriceTemplate = {
  operator: "",
  price: 0,
};

const boosterProbability = {
  boosterId: "",
  probability: 100,
};

const dropProbability = {
  ovrMin: 1,
  ovrMax: 100,
  probability: 100,
};

const playerPool = {
  playerCard: "",
  probability: 0,
};

const slotTemplate = {
  type: "",
  dropProbabilities: [],
  boosterProbabilities: [],
  playerCard: {},
  amount: 0,
  playerPool: [],
  playerPosition: [],
  tags: [],
};

let packTemplate: any = {
  name: "",
  type: "",
  summary: "",
  isDisabled: false,
  price: 0,
  requiredVaultLevel: null,
  slots: [slotTemplate],
  isFTUPack: false,
  fileType: "",
  packPredefinedImageType: "",
  imageUrl: "",
  nameTrans: {},
  summaryTrans: {},
  category: "",
  excludePlayers: [],
  operatorPackPrice: [],
  operators: [],
  operatorPacks: {},
};

const modify = (slot: any) => {
  switch (slot.type) {
    case "Player":
      return {
        tags: slot.tags,
        playerPosition: slot.playerPosition,
        dropProbabilities: slot.dropProbabilities?.map((dp) => {
          return {
            probability: Number(dp?.probability ?? 0),
            ovrMin: Number(dp?.ovrMin ?? 0),
            ovrMax: Number(dp?.ovrMax ?? 0),
          };
        }),
      };
    case "NamedPlayer":
      return {
        playerCard: slot.playerCard?.id,
      };
    case "Currency":
      return {
        amount: Number(slot.amount),
      };
    case "Booster":
      return {
        boosterProbabilities: slot.boosterProbabilities?.map((bp) => {
          return {
            probability: Number(bp?.probability ?? 0),
            boosterId: bp?.boosterId?.id ?? bp?.boosterId,
          };
        }),
      };
    case "PlayerPool":
      return {
        playerPool: slot.playerPool.map((item) => ({
          playerCard: item?.playerCard?.id,
          probability: Number(item?.probability),
        })),
      };
    default:
      return slot;
  }
};

const PackEditor = () => {
  const styles = useStyles();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const id = location.pathname.split("/").slice(-1)[0];
  const navigate = useNavigate();
  const [packDetails, setPackDetails] = useState(packTemplate);
  const [loading, setLoading] = useState(false);
  const { isOpen, close, open } = useOpenClose();
  const [options, setOptions] = useState({
    slotCount: 1,
    packTypeOptions: [
      { id: "Reward", label: "Reward" },
      { id: "Store", label: "Store" },
    ],
    operatorOptions: Array.from({ length: 5 }, (v, k) => ({
      id: "id_" + (k + 1),
      label: "Operator_" + (k + 1),
    })),
    noOfSlotOptions: Array.from({ length: 30 }, (v, k) => ({
      id: k + 1,
      label: k + 1,
    })),
    slotTypeOptions: [
      { id: "Player", label: "Player" },
      { id: "NamedPlayer", label: "Named Player" },
      { id: "Currency", label: "Currency" },
      { id: "Booster", label: "Booster" },
      { id: "PlayerPool", label: "Player Pool" },
    ],
    packCategories: [],
    packCategoryOptions: [],
    images: [],
    selectedPackImage: null,
    playerCards: [],
    playerCardOptions: [],
    boosters: [],
    boostersOptions: [],
    rewardPacks: [],
    rewardPackOptions: [],
  });
  const [modalData, setModalData] = useState({
    language: "",
    inputValue: "",
    field: null,
    viewData: [],
  });
  const [imageDimensions, setImageDimensions] = useState<{
    targetWidth: number;
    targetHeight: number;
    originalWidth: number;
    originalHeight: number;
  } | null>(null);
  const [playerTag, setPlayerTag] = useState({});
  const [validationDialogData, setValidationDialogData] = useState({
    open: false,
    errorMessages: [],
    validationResults: [],
  });
  const [slotValidationErrors, setSlotValidationErrors] = useState({});
  const [translationFile, setTranslationFile] = useState<File | null>(null);
  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.PLAYER.PACK.DELETE_ROLES,
  );
  const hasValidatedOnLoad = useRef(false);

  const onPlayerTagChange = (e, slotIndex) => {
    const { value } = e.target;
    if (value !== ",")
      setPlayerTag((state) => ({ ...state, [slotIndex]: value }));
  };

  const getSlotTypeAndName = (slotIndex: any) => {
    let returnData = {
      type: packDetails.slots[slotIndex].type,
      name: "",
    };

    const { type } = returnData;

    if (type === "Player") returnData.name = "dropProbabilities";
    if (type === "PlayerPool") returnData.name = "playerPool";
    if (type === "Booster") returnData.name = "boosterProbabilities";

    return returnData;
  };

  const cancel = () => navigate(configData.routes.packs.packs);

  const submit = async () => {
    try {
      setLoading(true);
      let resp;

      let pack = deepCopy({
        ...packDetails,
        excludePlayers: packDetails.excludePlayers.map((player) => player.id),
      });

      delete pack?.playerDetails;
      if (options.selectedPackImage) {
        pack.fileType = "image/webp";
        delete pack?.packPredefinedImageType;
      }

      await packSchema.validate(pack, { abortEarly: false });
      delete pack?.imageUrl;

      let _slots = pack.slots.map((slot) => {
        let slotData = modify(slot);
        return {
          type: slot.type,
          ...slotData,
        };
      });

      const { category, ...rest } = pack;
      const operatorPackPrice = packDetails?.operatorPackPrice?.reduce(
        (acc, curr) => {
          acc[`${curr?.operator}`] = Number(curr?.price);
          return acc;
        },
        {},
      );

      if (id !== "create") {
        resp = await updatePack2({
          ...rest,
          slots: _slots,
          categoryId: category,
          operatorPackPrice: operatorPackPrice,
          operatorPacks: packDetails.operatorPacks,
        });
      } else {
        resp = await createPack2({
          ...rest,
          slots: _slots,
          categoryId: category,
          operatorPackPrice: operatorPackPrice,
          operatorPacks: packDetails.operatorPacks,
        });
      }

      if (resp?.data?.fileUploadUrl) {
        await upload(resp?.data?.fileUploadUrl);
      }

      if (id === "create" && translationFile && resp?.data?._id) {
        await uploadContentTranslations(translationFile, "PACK", resp.data._id);
      }

      setOptions((st) => ({ ...st, selectedPackImage: null }));
      setSlotValidationErrors({}); // Clear validation errors on successful save

      // Check if pack has validation errors (created/updated successfully but has invalid slots)
      if (resp?.data?.isValid === false) {
        const warningMessage =
          id !== "create"
            ? "Pack updated successfully, but some slots contain errors. Fix the errors before enabling the pack."
            : "Pack created successfully, but some slots contain errors. Fix the errors before enabling the pack.";
        showSnackbar(warningMessage, "warning");
      } else {
        showSnackbar(resp?.message, "success");
      }

      navigate(configData.routes.packs.packs);
    } catch (err) {
      // Handle pack slot validation errors from backend
      const errorDetails = err?.response?.data?.details;

      if (errorDetails?.code === 4007) {
        // INVALID_PACK_SLOT_CONFIGURATION - Show inline errors in slots
        const metadata = errorDetails.metadata || {};
        const validationResults = metadata.validationResults || [];

        // Map validation results to slot errors
        const slotErrors = {};
        validationResults.forEach((result) => {
          if (!result.isValid) {
            slotErrors[result.slotIndex] = result;
          }
        });

        setSlotValidationErrors(slotErrors);
        setValidationDialogData({
          open: true,
          errorMessages: metadata.errors || [],
          validationResults: metadata.validationResults || [],
        });
        showSnackbar(
          "Pack has invalid slot configurations. Please fix the errors shown below.",
          "error",
        );
      } else if (errorDetails?.code === 4008) {
        // CANNOT_ENABLE_INVALID_PACK - Show validation dialog (blocking operation)
        const metadata = errorDetails.metadata || {};

        setValidationDialogData({
          open: true,
          errorMessages: metadata.errors || [],
          validationResults: metadata.validationResults || [],
        });
      } else if (err.inner) {
        // Yup validation errors
        err.inner?.forEach((error) => {
          showSnackbar(error.message, "error");
        });
      } else {
        // Generic error handling
        showSnackbar(
          err?.response?.data?.message || err.message || "An error occurred",
          "error",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const updateSlotCount = (e: any) => {
    setLoading(true);
    const _value = Number(e.target.value);
    let pack = { ...packDetails };

    if (_value > options.slotCount) {
      pack.slots = [
        ...pack.slots,
        ...Array.from(
          { length: _value - options.slotCount },
          (_, k) => slotTemplate,
        ),
      ];
    } else {
      pack.slots = pack.slots.slice(0, _value);
    }

    setOptions((st) => ({ ...st, slotCount: _value }));
    setPackDetails(pack);
    setLoading(false);
  };

  const onChange = (e: any) => {
    const { name, value } = e?.target;

    let parsedValue: any = value;
    if (name === "price") {
      parsedValue = Number(value);
    } else if (name === "requiredVaultLevel") {
      // Empty input clears the restriction (null = no vault gate).
      parsedValue = value === "" || value === null ? null : Number(value);
    }

    let data = {
      ...packDetails,
      [name]: parsedValue,
    };

    if (["name", "summary"].includes(name)) {
      data[`${name}Trans`]["en"] = value;
    }

    if (name === "type") {
      if (value === "Store") {
        // Clear operator packs when switching to Store
        data.operatorPacks = {};
        if (!data.operatorPackPrice.length) {
          data.operatorPackPrice = [operatorPriceTemplate];
        }
      } else if (value === "Reward") {
        // Clear operators and vault gating when switching to Reward
        data.operators = [];
        data.operatorPackPrice = [];
        data.requiredVaultLevel = null;
      }
    }

    setPackDetails((_state) => ({ ..._state, ...data }));
  };

  const onPlayerPosAndTagChange = (e, slotIndex) => {
    const { name, value } = e.target;

    setPackDetails((state) => {
      const currentTags = state.slots[slotIndex].tags || [];
      let _tags = currentTags;

      if (name === "tags") {
        if (
          (e.key === "Enter" || e.key === ",") &&
          playerTag[slotIndex].trim()
        ) {
          if (!currentTags.includes(playerTag[slotIndex].trim()))
            _tags.push(playerTag[slotIndex].trim());

          setPlayerTag((state) => ({ ...state, [slotIndex]: "" })); // Reset playerTag
        }
      }

      return {
        ...state,
        slots: [
          ...state.slots.slice(0, slotIndex),
          {
            ...state.slots[slotIndex],
            [name]: name === "tags" ? _tags : value,
          },
          ...state.slots.slice(slotIndex + 1),
        ],
      };
    });
  };

  const handleDeleteTag = (tagToDelete, slotIndex) => {
    setPackDetails((state) => ({
      ...state,
      slots: [
        ...packDetails.slots.slice(0, slotIndex),
        {
          ...packDetails.slots[slotIndex],
          tags: packDetails.slots[slotIndex].tags.filter(
            (tag) => tag !== tagToDelete,
          ),
        },
        ...packDetails.slots.slice(slotIndex + 1),
      ],
    }));
  };

  const onCurrencyChange = (e, sIndex) => {
    const { value } = e.target;
    let pack = { ...packDetails };
    pack.slots[sIndex].amount = value;
    setPackDetails(pack);
  };

  const handleSlotType = async (e, slotIndex) => {
    const { name, value } = e.target;

    let slotDetails = {
      ...packDetails.slots[slotIndex],
      ...slotTemplate,
      [name]: value,
    };

    let updatedData = [];

    switch (value) {
      case "Player":
        updatedData = [dropProbability];
        if (packDetails.slots[slotIndex].dropProbabilities.length) {
          updatedData = packDetails.slots[slotIndex].dropProbabilities;
        }
        slotDetails = {
          ...slotDetails,
          dropProbabilities: updatedData,
          tags: [...slotDetails.tags],
          playerPosition: [...slotDetails.playerPosition],
        };
        break;
      case "PlayerPool":
        updatedData = [playerPool];
        if (packDetails.slots[slotIndex].playerPool.length) {
          updatedData = packDetails.slots[slotIndex].playerPool;
        }
        slotDetails = {
          ...slotDetails,
          playerPool: updatedData,
        };
        break;
      case "Booster":
        updatedData = [boosterProbability];
        if (packDetails.slots[slotIndex].boosterProbabilities.length) {
          updatedData = packDetails.slots[slotIndex].boosterProbabilities;
        }
        slotDetails = {
          ...slotDetails,
          boosterProbabilities: updatedData,
        };
        break;
      default:
        // Handle unexpected cases or leave empty if nothing needs to be done
        console.warn(`Unexpected value: ${value}`);
    }

    setPackDetails((st) => ({
      ...st,
      slots: [
        ...packDetails.slots.slice(0, slotIndex),
        {
          ...slotDetails,
        },
        ...packDetails.slots.slice(slotIndex + 1),
      ],
    }));
  };

  const onDropAndBoosterChange = (e, slotIndex, nestedIndex) => {
    const { name, value } = e.target;
    const { name: _nameToChange, type } = getSlotTypeAndName(slotIndex);
    if (!PlayerAndBooster.includes(type)) return;

    // Clear validation error for this slot when user makes changes
    if (slotValidationErrors[slotIndex]) {
      setSlotValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[slotIndex];
        return newErrors;
      });
    }

    setPackDetails((state) => ({
      ...state,
      slots: [
        ...packDetails.slots.slice(0, slotIndex),
        {
          ...packDetails.slots[slotIndex],
          [_nameToChange]: [
            ...packDetails.slots[slotIndex][_nameToChange]?.slice(
              0,
              nestedIndex,
            ),
            {
              ...packDetails.slots[slotIndex][_nameToChange][nestedIndex],
              [name]: value,
            },
            ...packDetails.slots[slotIndex][_nameToChange]?.slice(
              nestedIndex + 1,
            ),
          ],
        },
        ...packDetails.slots.slice(slotIndex + 1),
      ],
    }));
  };

  const onPlayerPoolAndNamedPlayerChange = (e, slotIndex, ppIndex) => {
    const { name, value } = e.target;

    const { name: _nameToChange, type } = getSlotTypeAndName(slotIndex);

    let playerPoolData = () => {
      return [
        ...packDetails.slots[slotIndex].playerPool?.slice(0, ppIndex),
        {
          ...packDetails.slots[slotIndex].playerPool[ppIndex],
          [name]: value,
        },
        ...packDetails.slots[slotIndex].playerPool?.slice(ppIndex + 1),
      ];
    };

    const _name = type === "PlayerPool" ? _nameToChange : name;
    const pack = {
      ...packDetails,
      slots: [
        ...packDetails.slots.slice(0, slotIndex),
        {
          ...packDetails.slots[slotIndex],
          [_name]: type === "PlayerPool" ? playerPoolData() : value,
        },
        ...packDetails.slots.slice(slotIndex + 1),
      ],
    };

    setPackDetails(pack);
  };

  const add = (slotIndex: any) => {
    const { name, type } = getSlotTypeAndName(slotIndex);
    let _buildpack = (value) => ({
      ...packDetails,
      slots: [
        ...packDetails.slots.slice(0, slotIndex),
        {
          ...packDetails.slots[slotIndex],
          [name]: [...packDetails.slots[slotIndex][name], value],
        },
        ...packDetails.slots.slice(slotIndex + 1),
      ],
    });

    let pack;

    if (type === "Player") pack = _buildpack(dropProbability);
    if (type === "PlayerPool") pack = _buildpack(playerPool);
    if (type === "Booster") pack = _buildpack(boosterProbability);

    setPackDetails(pack);
  };

  const removeOne = (slotIndex, nestedIndex) => {
    const { name } = getSlotTypeAndName(slotIndex);
    let pack = { ...packDetails };
    pack.slots[slotIndex][name] = pack.slots[slotIndex][name]?.filter(
      (_, _nestIdx) => _nestIdx !== nestedIndex,
    );
    setPackDetails(pack);
  };

  const removeAll = (slotIndex: any) => {
    const { name, type } = getSlotTypeAndName(slotIndex);
    let pack = { ...packDetails };
    pack.slots[slotIndex][name] =
      type === "Player" ? [dropProbability] : [boosterProbability];
    setPackDetails(pack);
  };

  const onSelectImage = (image: any, dimensions?: any) => {
    setOptions((st) => ({ ...st, selectedPackImage: image ? image : null }));
    if (dimensions) {
      setImageDimensions(dimensions);
    }
    setPackDetails((state) => ({ ...state, packPredefinedImageType: "" }));
  };

  const upload = async (url) => {
    try {
      const defaultDims = DEFAULT_IMAGE_DIMENSIONS[ImageType.PACK_IMAGE];
      const resp = await uploadImageV2({
        data: options.selectedPackImage,
        uploadUrl: url,
        resize: true,
        resizeToWidth: imageDimensions?.targetWidth || defaultDims.width,
        resizeToHeight: imageDimensions?.targetHeight || defaultDims.height,
      });

      showSnackbar("Image Uploaded", "success");
      return resp;
    } catch (error) {
      showSnackbar("Error uploading image", "error");
    }
  };

  const fetchBoosters = async (filters?: any) => {
    try {
      const resp = await getBoosters();
      const boosterOptions = resp.data?.map((boost) => ({
        id: boost?._id,
        label: boost?.displayName,
      }));
      setOptions((prevState) => {
        return {
          ...prevState,
          boostersOptions: boosterOptions,
        };
      });
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const onBoosterSearchChange = async (searchValue) => {
    if (searchValue !== "") {
      fetchBoosters({
        pageNo: 1,
        pageSize: 10,
        search: searchValue,
        skipPagination: true,
      });
    } else {
      setOptions((prev) => {
        return {
          ...prev,
          boostersOptions: prev.boosters?.map((boost) => ({
            id: boost?._id,
            label: boost?.displayName,
          })),
        };
      });
    }
  };

  const handleOpenModal = (type, field) => {
    setModalData((state) => {
      if (type === "view") {
        let data = getTranslatedData(field);
        return { ...state, viewData: data, field, modal: type };
      } else {
        return {
          ...state,
          field,
          modal: type,
          language: "",
          inputValue: "",
        };
      }
    });
    open();
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setModalData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLangEditClick = (data: any) => {
    close();
    setModalData((state) => ({
      ...state,
      language: data?.lang,
      inputValue: data?.inputValue,
      modal: "add",
    }));
    open();
  };

  const handleLangDeleteClick = (data: any) => {
    if (data.lang === "en") {
      showSnackbar(
        "Cannot delete English translation, as it's a default languange",
        "error",
      );
      return;
    }

    // remove from view data
    setModalData((state) => ({
      ...state,
      viewData: state.viewData.filter((item) => item.lang !== data.lang),
    }));

    setPackDetails((prev) => {
      prev[modalData.field] = Object.keys(prev[modalData.field]).reduce(
        (acc, key) => {
          if (key !== data.lang) {
            acc[key] = prev[modalData.field][key];
          }
          return acc;
        },
        {},
      );
      return { ...prev };
    });
  };

  const handleAddTranslation = () => {
    const { field } = modalData;
    if (!field) {
      showSnackbar("Confused which field to add the translation", "error");
      return;
    }

    setPackDetails((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [modalData.language]: modalData.inputValue,
      },
    }));

    // if english translation is available set it field
    if (modalData.language === "en" && modalData.inputValue !== "") {
      setPackDetails((_) => ({
        ..._,
        [field?.split("T")[0]]: modalData.inputValue,
      }));
    }
    setModalData((prev) => ({
      ...prev,
      inputValue: "",
      language: "",
      field: null,
    }));
    close();
  };

  const handlePackChange = (e: any) => {
    const { value } = e.target;
    // Handle SearchableInput format (array of objects with id/label)
    if (Array.isArray(value)) {
      const operatorIds = value.map((item) =>
        typeof item === "object" ? item.id : item,
      );
      setPackDetails((prev) => ({
        ...prev,
        operators: operatorIds,
      }));
    } else {
      // Handle other input formats
      setPackDetails((prev) => ({
        ...prev,
        operators: value ? [value] : [],
      }));
    }
  };

  const getTranslatedData = (field: any) => {
    const objEntries = Object.entries(packDetails[field]);
    if (!objEntries?.length) return [];
    const _translatedData = objEntries?.map(([key, value]) => ({
      lang: key,
      inputValue: value,
    }));
    if (!_translatedData) return [];
    return _translatedData;
  };

  const fetchPlayerCards = async (filters, defaultMode?: boolean) => {
    try {
      const resp = await getPlayerCards(filters);
      let newPlayerCardsOps = resp?.items?.map((player) => {
        return {
          id: player?._id,
          label: buildLabel(player),
        };
      });

      setOptions((state) => {
        return {
          ...state,
          playerCards: getUniquesArrayObject(
            [...resp?.items, ...state.playerCards],
            "_id",
          ),
          playerCardOptions: getUniquesArrayObject([
            ...newPlayerCardsOps,
            ...state.playerCardOptions,
          ]),
        };
      });
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const onPlayerCardSearch = async (searchValue) => {
    if (searchValue !== "") {
      fetchPlayerCards({
        pageSize: defaultPageSize,
        pageNo: defaultPageNo,
        search: searchValue,
        skipPagination: true,
      });
    } else {
      fetchPlayerCards(
        { pageSize: defaultPageSize, pageNo: defaultPageNo },
        true,
      );
    }
  };

  const optimizeSearch = debounce(onPlayerCardSearch, 300);

  const onOperatorPriceChange = (e, idx) => {
    const { name, value } = e.target;
    setPackDetails((state) => {
      return {
        ...state,
        operatorPackPrice: [
          ...state.operatorPackPrice.slice(0, idx),
          {
            ...state.operatorPackPrice[idx],
            [name]: value,
          },
          ...state.operatorPackPrice.slice(idx + 1),
        ],
      };
    });
  };

  const deleteOperatorPriceConfig = (idx: any) => {
    setPackDetails((state) => {
      return {
        ...state,
        operatorPackPrice:
          idx === "all"
            ? []
            : state.operatorPackPrice.filter((_, i) => i !== idx),
      };
    });
  };

  const addOperatorPriceConfig = () => {
    setPackDetails((state) => {
      return {
        ...state,
        operatorPackPrice: [...state.operatorPackPrice, operatorPriceTemplate],
      };
    });
  };

  // Operator Pack Management Functions for Reward type packs
  const addOperatorPack = () => {
    const availableOperators =
      sessionHelper.operatorOptions?.filter(
        (op) => !Object.keys(packDetails.operatorPacks).includes(op.id),
      ) || [];

    if (availableOperators.length === 0) {
      showSnackbar("No more operators available to add", "warning");
      return;
    }

    // Use a temporary key to show placeholder instead of auto-selecting
    const tempKey = `temp_${Date.now()}`;
    setPackDetails((state) => ({
      ...state,
      operatorPacks: {
        ...state.operatorPacks,
        [tempKey]: "",
      },
    }));
  };

  const removeOperatorPack = (operatorId: any) => {
    setPackDetails((state: any) => {
      const { [operatorId]: removedOperator, ...remainingOperatorPacks } =
        state.operatorPacks;
      return {
        ...state,
        operatorPacks: remainingOperatorPacks,
      };
    });
  };

  const updateOperatorPack = (oldOperatorId, newOperatorId, packId) => {
    if (oldOperatorId === newOperatorId) {
      // Just update the pack ID
      setPackDetails((state) => ({
        ...state,
        operatorPacks: {
          ...state.operatorPacks,
          [oldOperatorId]: packId,
        },
      }));
    } else {
      // Change operator ID or handle temp key selection
      setPackDetails((state: any) => {
        const { [oldOperatorId]: removedOperator, ...remainingOperatorPacks } =
          state.operatorPacks;

        // If newOperatorId is empty (placeholder selection), don't add anything
        if (!newOperatorId) {
          return {
            ...state,
            operatorPacks: remainingOperatorPacks,
          };
        }

        return {
          ...state,
          operatorPacks: {
            ...remainingOperatorPacks,
            [newOperatorId]: packId,
          },
        };
      });
    }
  };

  const getAvailableOperators = (currentOperatorId = null) => {
    const usedOperatorIds = Object.keys(packDetails.operatorPacks).filter(
      (id) => !id.startsWith("temp_"), // Exclude temporary keys
    );

    return (
      sessionHelper.operatorOptions?.filter(
        (op) => op.id === currentOperatorId || !usedOperatorIds.includes(op.id),
      ) || []
    );
  };

  const fetchRewardPacks = async () => {
    try {
      const response = await getPlayerPacks({
        pageNo: 1,
        pageSize: 100,
        sortBy: "updatedAt",
        sortDir: "DESC",
      });

      // Filter only reward type packs
      const rewardPacks =
        response.items?.filter((pack) => pack.type === "Reward") || [];

      const rewardPackOptions = rewardPacks.map((pack) => ({
        id: pack._id,
        label: pack.name,
        idFromExcel: pack.idFromExcel,
        teamNameExcel: pack.teamNameExcel,
      }));

      setOptions((state) => ({
        ...state,
        rewardPacks: rewardPacks,
        rewardPackOptions: rewardPackOptions,
      }));
    } catch (error) {
      showSnackbar(error.message, "error");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log("PackEditor: Starting fetchData, id:", id);
      const promises = [
        getBoosters(),
        getPackImages(null),
        getPackCategories(),
        getPlayerCards({ pageNo: 1, pageSize: 10 }), // fetching some player card data initially
      ];

      if (id !== "create") promises.push(packInfo(id));

      // Fetch reward packs separately as it doesn't need to be in Promise.all
      fetchRewardPacks();

      const responses = await Promise.all(promises);

      let boosterRes,
        packImagesRes,
        packCategoriesRes,
        playerCardRes,
        packInfoRes;

      if (id !== "create") {
        [
          boosterRes,
          packImagesRes,
          packCategoriesRes,
          playerCardRes,
          packInfoRes,
        ] = responses;
        console.log("PackEditor: Edit mode - packInfoRes:", packInfoRes);
      } else {
        [boosterRes, packImagesRes, packCategoriesRes, playerCardRes] =
          responses;
        console.log("PackEditor: Create mode");
      }

      const boosterOptions = boosterRes.data?.map((boost) => ({
        id: boost?._id,
        label: boost?.displayName,
      }));

      const packImagesOptions = packImagesRes.data?.map((obj) => ({
        ...obj,
        id: obj?.name,
        label: obj.name,
      }));

      const packCategoryOptions = packCategoriesRes?.data?.map((el) => {
        return {
          id: el?._id,
          label: el?.name,
        };
      });

      let playerCardsOps = playerCardRes?.items?.map((player) => {
        return {
          id: player?._id,
          label: buildLabel(player),
        };
      });

      let mergedPlayerCards = [...playerCardRes?.items];
      let mergedPlayerCardsOps = [...playerCardsOps];

      if (packInfoRes && packInfoRes?.data) {
        const keys = Object.keys(packInfoRes.data.playerDetails);
        const playerDetailsCards = keys.map((key) => ({
          _id: key,
          ...packInfoRes.data.playerDetails[key],
        }));

        for (let pc of playerDetailsCards) {
          const _player = mergedPlayerCards.find((el) => el._id === pc._id);
          if (!_player) mergedPlayerCards.push(pc);
        }

        const _playerDetailsCardOptions = playerDetailsCards.map((pc) => ({
          id: pc?._id,
          label: buildLabel(pc),
        }));

        mergedPlayerCardsOps = getUniquesArrayObject(
          [...mergedPlayerCardsOps, ..._playerDetailsCardOptions],
          "id",
        );

        const slots = packInfoRes.data?.slots.map((slot, _index) => {
          if (slot?.type === "NamedPlayer") {
            let player = packInfoRes?.data?.playerDetails[slot.playerCard];
            return {
              ...slot,
              playerCard: {
                id: slot.playerCard,
                label: buildLabel(player),
              },
            };
          } else if (slot?.type === "PlayerPool") {
            return {
              ...slot,
              playerPool: slot.playerPool?.map((pc) => {
                let player = mergedPlayerCardsOps.find(
                  (el) => el.id === pc.playerCard,
                );
                return {
                  ...pc,
                  playerCard: {
                    id: pc?.playerCard,
                    label: player?.label,
                  },
                };
              }),
            };
          }
          return slot;
        });

        const nameTrans = { ...packInfoRes.data?.nameTrans };
        const summaryTrans = { ...packInfoRes.data?.summaryTrans };

        if (packInfoRes?.data?.name && !nameTrans["en"]) {
          nameTrans["en"] = packInfoRes?.data?.name;
        } else if (packInfoRes?.data) {
          packInfoRes.data.name = nameTrans["en"];
        }
        if (packInfoRes?.data?.summary && !summaryTrans["en"]) {
          summaryTrans["en"] = packInfoRes?.data?.summary;
        } else if (packInfoRes?.data) {
          packInfoRes.data.summary = summaryTrans["en"];
        }

        const excludePlayers = packInfoRes.data.excludePlayers?.map((pid) => {
          const _player =
            mergedPlayerCardsOps[
              mergedPlayerCardsOps?.findIndex((el) => el.id === pid)
            ];
          return { id: pid, label: _player?.label };
        });

        const _opPackPrices = packInfoRes?.data?.operatorPackPrice ?? {};
        const updateOperatorPrices = [];
        for (const [operator, price] of Object.entries(_opPackPrices))
          updateOperatorPrices.push({ operator, price });

        // Handle operatorPacks for existing pack data
        const operatorPacks = packInfoRes?.data?.operatorPacks ?? {};

        console.log("PackEditor: About to setPackDetails with data:", {
          packInfoData: packInfoRes.data,
          slots,
          nameTrans,
          summaryTrans,
          excludePlayers,
          operatorPackPrice: updateOperatorPrices,
          operatorPacks,
        });

        setPackDetails((state) => {
          return {
            ...state,
            ...packInfoRes.data,
            slots: slots,
            nameTrans: nameTrans,
            summaryTrans: summaryTrans,
            excludePlayers: excludePlayers,
            category: packInfoRes?.data?.category,
            operatorPackPrice: updateOperatorPrices ?? [],
            operatorPacks: operatorPacks,
          };
        });
      }

      setOptions((state) => {
        return {
          ...state,
          slotCount: id !== "create" ? packInfoRes?.data?.slots?.length : 1,
          images: packImagesOptions,
          boosters: boosterRes.data,
          boostersOptions: boosterOptions,
          packCategoryOptions: packCategoryOptions,
          playerCards: mergedPlayerCards,
          playerCardOptions: mergedPlayerCardsOps,
        };
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      showSnackbar(error.message, "error");
    }
  };

  // --- helper inside your component ---
  const findOption = (opts = [], val) =>
    opts?.find((o) => o?.value === val || o?.id === val) ?? null;

  const extractValueFromOptionOrEvent = (optOrEvt: any) => {
    if (!optOrEvt) return "";

    if (optOrEvt?.target && optOrEvt.target.value) {
      // when SearchableInput sends { target: { value: { id, label, ... } } }
      if (typeof optOrEvt.target.value === "object") {
        return optOrEvt.target.value.id; // or .value if your options are shaped differently
      }
      return optOrEvt.target.value;
    }

    if ("value" in optOrEvt && typeof optOrEvt.value === "object") {
      return optOrEvt.value.id;
    }

    if ("id" in optOrEvt) return optOrEvt.id;

    return "";
  };

  const getRewardOptions = (options: any) => {
    let updatedRewardPackOptions = options?.rewardPackOptions?.map(
      (data, index) => {
        return {
          label:
            data.label +
            (data.idFromExcel || data.teamNameExcel
              ? ` - ${[data.idFromExcel, data.teamNameExcel]
                  .filter(Boolean)
                  .join(" - ")}`
              : ""),
          id: data.id,
          idFromExcel: data.idFromExcel,
          teamNameExcel: data.teamNameExcel,
        };
      },
    );
    return updatedRewardPackOptions ?? [];
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Validate pack slots when pack is loaded in edit mode
  useEffect(() => {
    const validateSlotsOnLoad = async () => {
      // Only validate once and only in edit mode when slots are loaded
      if (
        hasValidatedOnLoad.current ||
        id === "create" ||
        !packDetails?.slots ||
        packDetails.slots.length === 0
      ) {
        return;
      }

      // Check if there are any Player type slots
      const hasPlayerSlots = packDetails.slots.some(
        (slot) => slot.type === "Player",
      );
      if (!hasPlayerSlots) {
        return;
      }

      try {
        // Mark as validated to prevent re-runs
        hasValidatedOnLoad.current = true;

        // Convert excludePlayers from {id, label} format to array of IDs
        const excludePlayerIds =
          packDetails.excludePlayers?.map((ep) => ep.id) || [];

        let pack = deepCopy(packDetails);

        let _slots = pack.slots.map((slot) => {
          let slotData = modify(slot);
          return {
            type: slot.type,
            ...slotData,
          };
        });

        const validationResp = await validatePackSlots(
          _slots,
          excludePlayerIds,
        );

        if (validationResp?.data && !validationResp.data.isValid) {
          // Map validation results to slot errors
          const slotErrors = {};
          validationResp.data.validationResults.forEach((result) => {
            if (!result.isValid) {
              slotErrors[result.slotIndex] = result;
            }
          });

          setSlotValidationErrors(slotErrors);

          if (Object.keys(slotErrors).length > 0) {
            showSnackbar(
              `This pack has ${
                Object.keys(slotErrors).length
              } invalid slot(s). Fix the errors before enabling.`,
              "warning",
            );
          }
        }
      } catch (error) {
        console.error("Error validating pack slots on load:", error);
        // Don't show error to user, validation is informational only
      }
    };

    validateSlotsOnLoad();
  }, [packDetails?.slots, packDetails?.excludePlayers, id]);

  const renderView = (slot, index) => {
    switch (slot?.type) {
      case "Player":
        return (
          <PlayerSlot
            slotData={slot}
            slotIndex={index}
            handleChange={onDropAndBoosterChange}
            removeOne={removeOne}
            validationError={slotValidationErrors[index]}
          />
        );
      case "NamedPlayer":
        return (
          <NamedPlayerSlot
            slotData={slot}
            slotIndex={index}
            handleChange={onPlayerPoolAndNamedPlayerChange}
            playerCards={options.playerCards}
            playerCardOptions={options.playerCardOptions}
            fetchPlayerCards={fetchPlayerCards}
          />
        );
      case "Booster":
        return (
          <BoosterSlot
            slotData={slot}
            slotIndex={index}
            options={options.boostersOptions}
            removeOne={removeOne}
            handleChange={onDropAndBoosterChange}
            onSearchChange={onBoosterSearchChange}
          />
        );
      case "Currency":
        return (
          <Box className={styles.formField}>
            <SimpleTextInput
              label="Amount"
              name="amount"
              value={packDetails.slots[index]?.amount || ""}
              onChange={(e) => onCurrencyChange(e, index)}
              type="number"
            />
          </Box>
        );
      case "PlayerPool":
        return (
          <PlayerPoolSlot
            slotData={slot}
            slotIndex={index}
            handleChange={onPlayerPoolAndNamedPlayerChange}
            playerCards={options.playerCardOptions}
            removeOne={removeOne}
            fetchPlayerCards={fetchPlayerCards}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <ContentShell
      showBackButton
      onBackButtonClick={() => navigate(-1)}
      title={`${id === "create" ? "Create" : "Edit"} Pack`}
      loading={loading}
      actions={
        <Box className={styles.buttonContainer}>
          <Button
            type="submit"
            variant="contained"
            style={{ backgroundColor: "red", color: "white" }}
            onClick={cancel}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" onClick={submit}>
            Save
          </Button>
        </Box>
      }
    >
      <Box className={styles.mainContainer}>
        {/* Pack Information Section */}
        <Box className={styles.sectionCard}>
          <Typography variant="h6" className={styles.sectionTitle}>
            Pack Information
          </Typography>
          <Divider className={styles.sectionDivider} />
          <Box className={styles.formGrid}>
            <Box className={styles.formField}>
              <SimpleTextInput
                name="name"
                label="Pack Name"
                value={packDetails.name}
                onChange={onChange}
                button={
                  <TranslationButtons
                    field={"nameTrans"}
                    onClick={handleOpenModal}
                  />
                }
              />
            </Box>
            <Box className={styles.formField}>
              <SimpleSelectInput
                label="Pack Type"
                name="type"
                value={packDetails.type}
                options={options.packTypeOptions}
                onChange={onChange}
              />
            </Box>
            {packDetails.type === "Store" && (
              <Box className={styles.formField}>
                <SimpleTextInput
                  name="price"
                  label="Pack Price"
                  value={packDetails.price}
                  onChange={onChange}
                />
              </Box>
            )}
            {packDetails.type === "Store" && (
              <Box className={styles.formField}>
                <SimpleTextInput
                  name="requiredVaultLevel"
                  label="Required Vault Level (leave blank for no restriction)"
                  value={packDetails.requiredVaultLevel ?? ""}
                  onChange={onChange}
                  type="number"
                />
              </Box>
            )}
            <Box className={styles.formField}>
              <SimpleTextInput
                label="Pack Summary"
                name="summary"
                value={packDetails.summary}
                onChange={onChange}
                button={
                  <TranslationButtons
                    field={"summaryTrans"}
                    onClick={handleOpenModal}
                  />
                }
              />
            </Box>
            <Box className={styles.formField}>
              <SimpleSelectInput
                label="Territory"
                name="territory"
                value={packDetails?.territory || ""}
                options={sessionHelper.countryOptions}
                onChange={onChange}
              />
            </Box>
            <Box className={styles.formField}>
              <SimpleSelectInput
                label="Pack Category"
                name="category"
                value={packDetails.category ?? ""}
                onChange={onChange}
                options={options?.packCategoryOptions || []}
              />
            </Box>
            {packDetails?.idFromExcel && (
              <Box className={styles.formField}>
                <SimpleTextInput
                  label="Id Excel"
                  name="idFromExcel"
                  value={packDetails?.idFromExcel}
                  readOnly={true}
                  readonlyvalue={packDetails?.idFromExcel}
                />
              </Box>
            )}
            <Box className={styles.formFieldWide}>
              <SearchableInput
                selected={packDetails.excludePlayers}
                isMultiSelect={true}
                label={"Exclude Player"}
                name={"excludePlayers"}
                options={options.playerCardOptions ?? []}
                onInputChange={optimizeSearch}
                onOptionSelect={onChange}
                placeholder="Select players"
                loading={loading}
              />
            </Box>
          </Box>
        </Box>

        {/* Pack Image Section */}
        <Box className={styles.sectionCard}>
          <Typography variant="h6" className={styles.sectionTitle}>
            Pack Image
          </Typography>
          <Divider className={styles.sectionDivider} />
          <Box className={styles.imageSection}>
            {!options.selectedPackImage && (
              <Box className={styles.formField}>
                <SimpleSelectInput
                  label="Predefined Pack Image"
                  name="packPredefinedImageType"
                  options={options.images}
                  value={packDetails?.packPredefinedImageType || ""}
                  onChange={onChange}
                />
              </Box>
            )}
            <Box className={styles.imagePickerContainer}>
              <FilePicker
                label="Upload Custom Image"
                value={options.images.find(
                  (image) => image.id === packDetails.packPredefinedImageType,
                )}
                name="packImage"
                onFileSelect={onSelectImage}
                imageType={ImageType.PACK_IMAGE}
                enableDimensionControl={true}
                metaData={{
                  note: `Recommended: ${
                    DEFAULT_IMAGE_DIMENSIONS[ImageType.PACK_IMAGE].width
                  } x ${
                    DEFAULT_IMAGE_DIMENSIONS[ImageType.PACK_IMAGE].height
                  } pixels. Images will be resized and converted to WebP.`,
                }}
              />
              {packDetails?.imageUrl &&
                !options.selectedPackImage &&
                !packDetails.packPredefinedImageType && (
                  <Box className={styles.imagePreviewContainer}>
                    <Typography
                      variant="body2"
                      className={styles.imagePreviewLabel}
                    >
                      Current Image:
                    </Typography>
                    <Box
                      component="img"
                      src={packDetails?.imageUrl}
                      alt="Pack Preview"
                      className={styles.imagePreview}
                    />
                  </Box>
                )}
            </Box>
          </Box>
        </Box>

        {/* Store Configuration Section */}
        {packDetails.type === "Store" && (
          <Box className={styles.sectionCard}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Store Configuration
            </Typography>
            <Divider className={styles.sectionDivider} />

            {/* Operator Price Configuration */}
            <Box className={styles.subsectionContainer}>
              <Typography
                variant="subtitle1"
                className={styles.subsectionTitle}
              >
                Operator Price Configuration
              </Typography>
              <Box className={styles.actionButtonsContainer}>
                <CustomButton
                  onClick={addOperatorPriceConfig}
                  variant="outlined"
                  size="small"
                >
                  Add Operator Price
                </CustomButton>
                <CustomButton
                  onClick={() => deleteOperatorPriceConfig("all")}
                  startIcon={<DeleteOutlineRounded />}
                  variant="outlined"
                  color="error"
                  size="small"
                >
                  Remove All
                </CustomButton>
              </Box>
              {packDetails?.operatorPackPrice?.map((op, idx) => (
                <Box key={idx} className={styles.operatorPriceRow}>
                  <Box className={styles.formField}>
                    <SimpleSelectInput
                      label="Operator"
                      name="operator"
                      value={op?.operator ?? ""}
                      options={sessionHelper.operatorOptions}
                      onChange={(e) => onOperatorPriceChange(e, idx)}
                    />
                  </Box>
                  <Box className={styles.formField}>
                    <SimpleTextInput
                      name="price"
                      label="Pack Price"
                      value={op?.price}
                      onChange={(e) => onOperatorPriceChange(e, idx)}
                    />
                  </Box>
                  {hasDeletePermission && (
                    <Box className={styles.deleteButtonContainer}>
                      <IconButton
                        onClick={() => deleteOperatorPriceConfig(idx)}
                        size="small"
                        className={styles.deleteButton}
                      >
                        <DeleteOutlineRounded />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            {/* Operators Selection */}
            <Box className={styles.subsectionContainer}>
              <Box className={styles.formFieldWide}>
                <SearchableInput
                  selected={(packDetails.operators || []).map((operatorId) => {
                    const operator = sessionHelper.operatorOptions?.find(
                      (op) => op.id === operatorId,
                    );
                    return {
                      id: operatorId,
                      label: operator?.label || operatorId,
                    };
                  })}
                  isMultiSelect={true}
                  label={"Operators (Optional)"}
                  name={"operators"}
                  options={sessionHelper.operatorOptions || []}
                  onOptionSelect={handlePackChange}
                  placeholder="Select operators (optional)"
                  loading={loading}
                />
              </Box>
            </Box>
          </Box>
        )}

        {/* Reward Configuration Section */}
        {packDetails.type === "Reward" && (
          <Box className={styles.sectionCard}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Reward Configuration
            </Typography>
            <Divider className={styles.sectionDivider} />

            <Box className={styles.subsectionContainer}>
              <Typography
                variant="subtitle1"
                className={styles.subsectionTitle}
              >
                Operator Packs (Optional)
              </Typography>
              {Object.entries(packDetails.operatorPacks).map(
                ([operatorId, packId], index) => (
                  <Box
                    key={`${operatorId}-${index}`}
                    className={styles.operatorPackRow}
                  >
                    <Box className={styles.formField}>
                      <SearchableInput
                        name="operator"
                        style={{ width: 300 }}
                        label="Operator"
                        selected={
                          operatorId.startsWith("temp_")
                            ? null
                            : findOption(
                                getAvailableOperators(
                                  operatorId.startsWith("temp_")
                                    ? null
                                    : operatorId,
                                ) ?? [],
                                operatorId,
                              )
                        }
                        options={
                          getAvailableOperators(
                            operatorId.startsWith("temp_") ? null : operatorId,
                          ) ?? []
                        }
                        placeholder="Select operator"
                        onOptionSelect={(optOrEvt) => {
                          const newOperatorId =
                            extractValueFromOptionOrEvent(optOrEvt) || "";
                          updateOperatorPack(operatorId, newOperatorId, packId);
                        }}
                        onInputChange={optimizeSearch}
                      />
                    </Box>

                    {/* Reward Pack SearchableInput */}
                    <Box className={styles.formField}>
                      <SearchableInput
                        name="rewardPack"
                        style={{ width: 500 }}
                        label="Reward Pack"
                        selected={findOption(
                          options.rewardPackOptions ?? [],
                          packId,
                        )}
                        options={getRewardOptions(options)}
                        placeholder="Select reward pack"
                        onOptionSelect={(optOrEvt) => {
                          const newPackId =
                            extractValueFromOptionOrEvent(optOrEvt) || "";
                          updateOperatorPack(operatorId, operatorId, newPackId);
                        }}
                      />
                    </Box>

                    <Box className={styles.deleteButtonContainer}>
                      <IconButton
                        onClick={() => removeOperatorPack(operatorId)}
                        size="small"
                        className={styles.deleteButton}
                      >
                        <DeleteOutlineRounded />
                      </IconButton>
                    </Box>
                  </Box>
                ),
              )}
              <CustomButton
                variant="outlined"
                onClick={addOperatorPack}
                disabled={
                  Object.keys(packDetails.operatorPacks).filter(
                    (id) => !id.startsWith("temp_"),
                  ).length >= (sessionHelper.operatorOptions?.length || 0)
                }
                size="small"
              >
                Add Operator Pack
              </CustomButton>
            </Box>
          </Box>
        )}
        {/* Slot Configuration Section */}
        <Box className={styles.sectionCard}>
          <Box className={styles.slotHeaderContainer}>
            <Typography variant="h6" className={styles.sectionTitle}>
              Slot Configuration
            </Typography>
            <Box className={styles.slotCountContainer}>
              <SimpleSelectInput
                label="Number of Slots"
                name="noOfSlots"
                options={options.noOfSlotOptions}
                value={options.slotCount}
                onChange={updateSlotCount}
              />
            </Box>
          </Box>
          <Divider className={styles.sectionDivider} />

          <Box className={styles.slotsContainer}>
            {Array.from({ length: options.slotCount }, (slot, k) => (
              <Box key={k} className={styles.slotCard}>
                <Box className={styles.slotHeader}>
                  <Box className={styles.slotHeaderLeft}>
                    <Typography
                      variant="subtitle1"
                      className={styles.slotTitle}
                    >
                      Slot {k + 1}
                    </Typography>
                    <Box className={styles.slotTypeContainer}>
                      <SimpleSelectInput
                        label="Slot Type"
                        name="type"
                        options={options.slotTypeOptions}
                        value={packDetails.slots[k]?.type ?? ""}
                        onChange={(e) => handleSlotType(e, k)}
                      />
                    </Box>
                  </Box>
                  <Box className={styles.slotHeaderRight}>
                    <AddAndDelete
                      isHidden={
                        packDetails.slots[k]?.type !== "PlayerPool" &&
                        !PlayerAndBooster.includes(packDetails.slots[k]?.type)
                      }
                      add={() => add(k)}
                      remove={() => removeAll(k)}
                    />
                  </Box>
                </Box>

                {/* Player Position and Tags for Player type slots */}
                {packDetails.slots[k]?.type === "Player" && (
                  <Box className={styles.playerConfigSection}>
                    <Box className={styles.playerConfigRow}>
                      <Box className={styles.formField}>
                        <SimpleMultiSelectInput
                          value={packDetails.slots[k]?.playerPosition ?? []}
                          name="playerPosition"
                          label="Player Position"
                          options={sessionHelper?.positionsOptions}
                          onChange={(e) => onPlayerPosAndTagChange(e, k)}
                        />
                      </Box>
                      <Box className={styles.playerTagsSection}>
                        <Box className={styles.tagInputContainer}>
                          <InputShell
                            label="Player Tags (Press Comma or Enter to add)"
                            name="tags"
                          >
                            <TextField
                              name="tags"
                              variant="outlined"
                              size="small"
                              placeholder="Add Player Tags"
                              value={playerTag[k] || ""}
                              onChange={(e) => onPlayerTagChange(e, k)}
                              onKeyDown={(e) => onPlayerPosAndTagChange(e, k)}
                              fullWidth
                            />
                          </InputShell>
                        </Box>
                        {packDetails.slots[k]?.tags?.length > 0 && (
                          <Box className={styles.tagsDisplayContainer}>
                            {packDetails.slots[k]?.tags?.map((tag, index) => (
                              <CustomChip
                                key={index}
                                label={tag}
                                onDelete={() => handleDeleteTag(tag, k)}
                                size="small"
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Slot Content */}
                <Box className={styles.slotContent}>
                  {renderView(packDetails.slots[k], k)}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 3 }}>
        <BulkTranslationUpload
          assetType="PACK"
          assetId={id !== "create" ? id : undefined}
          fields={[
            { label: "Name", fieldName: "nameTrans" },
            { label: "Summary", fieldName: "summaryTrans" },
          ]}
          {...(id === "create"
            ? { deferUpload: true, onFileChange: setTranslationFile }
            : { onSuccess: fetchData })}
        />
      </Box>
      <ViewLanguageModal
        label="Select Language"
        isOpen={isOpen}
        close={close}
        onChange={handleChange}
        handleOnSubmit={handleAddTranslation}
        actionHandlers={{
          handleLangEditClick: handleLangEditClick,
          handleLangDeleteClick: handleLangDeleteClick,
        }}
        modalData={modalData}
        tableColumns={[
          {
            id: "lang",
            label: "Language",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "inputValue",
            label: "Translation",
            alignHead: "center",
            alignBody: "center",
          },
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]}
      />

      <PackValidationDialog
        open={validationDialogData.open}
        errorMessages={validationDialogData.errorMessages}
        validationResults={validationDialogData.validationResults}
        onClose={() =>
          setValidationDialogData({
            open: false,
            errorMessages: [],
            validationResults: [],
          })
        }
      />
    </ContentShell>
  );
};

export default PackEditor;
